import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dropdown } from 'react-bootstrap';
import Select, {
    type FormatOptionLabelMeta,
    type MultiValue,
    type StylesConfig,
} from 'react-select';
import { useTranslation } from 'react-i18next';
import {
    TaskTypeStatus,
    type LeadType,
    type ObjectType,
    type TaskTypeDirType,
    type TaskTypeStyleType,
    type UserType,
} from '@econtractor/types';
import { useSettings } from 'context/SettingsContext';
import { useTheme } from 'context/ThemeContext';
import { selectStylesDark, selectStylesLigth } from 'utils/helpers/structs/selectStyles';
import { LeadSelected } from 'modules/lead';
import { ObjectSelected } from 'modules/objects';
import EToolTip from 'components/others/EToolTip';
import { UserSelected } from 'modules/user';
import { useTask } from '../../TaskContext';
import TaskStatusBadge from '../TaskStatusBadge';
import TaskTypeChip from '../TaskTypeChip';
import vconst from 'utils/helpers/structs/vconst';

/** У фільтрі задач: усі статуси заявок, крім Погоджено і Відмова */
const TASK_FILTER_LEAD_STATUSES = [
    vconst.leadNew,
    vconst.leadInWoork,
    vconst.leadNeedOffer,
    vconst.leadNeedsAttention,
    vconst.leadOffer,
];

/** У фільтрі задач: усі статуси об'єктів, крім Завершений і Відмінений */
const TASK_FILTER_OBJECT_STATUSES = [
    vconst.objectPanding,
    vconst.objectInWork,
    vconst.objectFinishedTeam,
];

interface FilterOption {
    value: number;
    label: string;
    kind: 'type' | 'status';
    icon?: string | null;
    colors?: TaskTypeStyleType | null;
}

const TASK_FILTER_STATUSES = [
    TaskTypeStatus.NEW,
    TaskTypeStatus.INWORK,
    TaskTypeStatus.COMPLETED,
    TaskTypeStatus.ACCEPTED,
    TaskTypeStatus.CANCELLED,
] as const;

/** Вище за Bootstrap offcanvas (~1045), щоб меню селекту було видно */
const MENU_Z_INDEX = 2000;

const buildSelectStyles = (dark: boolean): StylesConfig<FilterOption, true> => {
    const base = (dark ? selectStylesDark : selectStylesLigth) as StylesConfig<FilterOption, true>;

    return {
        ...base,
        control: (styles, state) => ({
            ...(base.control ? base.control(styles, state) : styles),
            minHeight: 38,
            fontSize: '0.8125rem',
            paddingBlock: 2,
        }),
        valueContainer: (styles, state) => ({
            ...(base.valueContainer ? base.valueContainer(styles, state) : styles),
            gap: 4,
            paddingTop: 2,
            paddingBottom: 2,
        }),
        indicatorsContainer: (styles) => ({
            ...styles,
            alignSelf: 'stretch',
        }),
        multiValue: () => ({
            display: 'flex',
            alignItems: 'center',
            backgroundColor: 'transparent',
            borderRadius: 0,
            margin: 0,
            padding: 0,
            maxWidth: '100%',
        }),
        multiValueLabel: () => ({
            padding: 0,
            fontSize: 'inherit',
            color: 'inherit',
            whiteSpace: 'nowrap',
        }),
        multiValueRemove: (styles) => ({
            ...styles,
            borderRadius: 6,
            padding: '0 4px',
            marginLeft: 2,
            color: dark ? '#9ca3af' : '#6b7280',
            ':hover': {
                backgroundColor: dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                color: dark ? '#fff' : '#111827',
            },
        }),
        menu: (styles, state) => ({
            ...(base.menu ? base.menu(styles, state) : styles),
            zIndex: MENU_Z_INDEX,
            fontSize: '0.8125rem',
            overflow: 'hidden',
        }),
        menuList: (styles, state) => ({
            ...(base.menuList ? base.menuList(styles, state) : styles),
            maxHeight: 240,
            paddingTop: 4,
            paddingBottom: 4,
        }),
        menuPortal: (styles) => ({
            ...styles,
            zIndex: MENU_Z_INDEX,
        }),
        option: (styles, state) => ({
            ...(base.option ? base.option(styles, state) : styles),
            fontSize: '0.8125rem',
            paddingTop: 8,
            paddingBottom: 8,
            paddingLeft: 10,
            paddingRight: 10,
            cursor: 'pointer',
            backgroundColor: state.isFocused
                ? dark
                    ? 'rgba(255,255,255,0.06)'
                    : '#F3F4F6'
                : state.isSelected
                  ? dark
                      ? 'rgba(70,128,255,0.18)'
                      : '#EEF4FF'
                  : 'transparent',
        }),
        placeholder: (styles) => ({
            ...styles,
            fontSize: '0.8125rem',
            color: dark ? '#8b95a1' : '#9ca3af',
        }),
    };
};

const formatTypeOption = (
    option: FilterOption,
    meta: FormatOptionLabelMeta<FilterOption>,
) => (
    <TaskTypeChip
        name={option.label}
        icon={option.icon}
        colors={option.colors}
        fontSize={meta.context === 'menu' ? '0.75rem' : '0.7rem'}
    />
);

const formatStatusOption = (option: FilterOption) => (
    <TaskStatusBadge status={option.value} />
);

type TaskFilterProps = {
    /** Статуси, які не показувати у фільтрі */
    excludeStatuses?: number[];
    /** Показати фільтр виконавця (UserSelected, одиночний вибір) */
    showExecutorFilter?: boolean;
    /** Показати фільтр контролера (UserSelected, одиночний вибір) */
    showControllerFilter?: boolean;
    /** Початковий виконавець для UI (snapshot з localStorage) */
    initialExecutorUser?: UserType | null;
    /** Початковий контролер для UI (snapshot з localStorage) */
    initialControllerUser?: UserType | null;
    initialLead?: LeadType | null;
    initialObject?: ObjectType | null;
    onExecutorUserChange?: (user: UserType | null) => void;
    onControllerUserChange?: (user: UserType | null) => void;
    onLeadChange?: (lead: LeadType | null) => void;
    onObjectChange?: (object: ObjectType | null) => void;
};

const snapshotToUser = (user: UserType | null | undefined): UserType | null => {
    if (!user?.ID) return null;
    return {
        ID: Number(user.ID),
        userName: String(user.userName || ''),
        userEmail: String(user.userEmail || ''),
        userPicture: String(user.userPicture || ''),
    } as UserType;
};

const TaskFilter = ({
    excludeStatuses,
    showExecutorFilter = false,
    showControllerFilter = false,
    initialExecutorUser = null,
    initialControllerUser = null,
    initialLead = null,
    initialObject = null,
    onExecutorUserChange,
    onControllerUserChange,
    onLeadChange,
    onObjectChange,
}: TaskFilterProps) => {
    const { t } = useTranslation(['Tasks', 'common']);
    const { darkTheme } = useTheme();
    const { localSettings } = useSettings();
    const {
        tasksStatus,
        setTasksStatus,
        tasksType,
        setTasksType,
        tasksExecutor,
        setTasksExecutor,
        tasksController,
        setTasksController,
        filterLeadId,
        setFilterLeadId,
        filterObjectId,
        setFilterObjectId,
        onlyPersonal,
        setOnlyPersonal,
        canFilterRelated,
        setPage,
    } = useTask();
    const [selectTheme, setSelectTheme] = useState(() => buildSelectStyles(Boolean(darkTheme)));
    const [show, setShow] = useState(false);
    const [executorUser, setExecutorUser] = useState<UserType | null>(() =>
        snapshotToUser(initialExecutorUser),
    );
    const [controllerUser, setControllerUser] = useState<UserType | null>(() =>
        snapshotToUser(initialControllerUser),
    );
    const [lead, setLead] = useState<LeadType | null>(() => initialLead?.ID ? initialLead : null);
    const [object, setObject] = useState<ObjectType | null>(() =>
        initialObject?.ID ? initialObject : null,
    );
    const [executorPickerOpen, setExecutorPickerOpen] = useState(() =>
        Boolean(snapshotToUser(initialExecutorUser)),
    );
    const [controllerPickerOpen, setControllerPickerOpen] = useState(() =>
        Boolean(snapshotToUser(initialControllerUser)),
    );
    const [leadPickerOpen, setLeadPickerOpen] = useState(() => Boolean(initialLead?.ID));
    const [objectPickerOpen, setObjectPickerOpen] = useState(() => Boolean(initialObject?.ID));
    const rootRef = useRef<HTMLSpanElement | null>(null);
    const excludeSet = useMemo(
        () => (excludeStatuses?.length ? new Set(excludeStatuses.map(Number)) : null),
        [excludeStatuses],
    );

    useEffect(() => {
        setSelectTheme(buildSelectStyles(Boolean(darkTheme)));
    }, [darkTheme]);

    useEffect(() => {
        if (!tasksExecutor?.length && executorUser) {
            setExecutorUser(null);
            setExecutorPickerOpen(false);
            onExecutorUserChange?.(null);
        }
    }, [tasksExecutor, executorUser, onExecutorUserChange]);

    useEffect(() => {
        if (!tasksController?.length && controllerUser) {
            setControllerUser(null);
            setControllerPickerOpen(false);
            onControllerUserChange?.(null);
        }
    }, [tasksController, controllerUser, onControllerUserChange]);

    useEffect(() => {
        if (!filterLeadId && lead) {
            setLead(null);
            setLeadPickerOpen(false);
            onLeadChange?.(null);
        }
    }, [filterLeadId, lead, onLeadChange]);

    useEffect(() => {
        if (!filterObjectId && object) {
            setObject(null);
            setObjectPickerOpen(false);
            onObjectChange?.(null);
        }
    }, [filterObjectId, object, onObjectChange]);

    useEffect(() => {
        if (!show) return;

        const onPointerDown = (event: MouseEvent) => {
            const target = event.target as HTMLElement | null;
            if (!target) return;

            if (rootRef.current?.contains(target)) return;

            if (
                target.closest('.task-filter-select__menu') ||
                target.closest('.task-filter-select__menu-portal')
            ) {
                return;
            }

            setShow(false);
        };

        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [show]);

    const typeOptions = useMemo<FilterOption[]>(
        () =>
            ((localSettings?.DIRS?.TaskType as TaskTypeDirType[] | undefined) || []).map((tt) => ({
                value: Number(tt.ID),
                label: String(tt.taskTypeName || tt.ID),
                kind: 'type' as const,
                icon: tt.taskTypeIcon || null,
                colors: tt.taslTypeJson || null,
            })),
        [localSettings?.DIRS?.TaskType],
    );

    const statusOptions = useMemo<FilterOption[]>(
        () =>
            TASK_FILTER_STATUSES.filter((statusId) => !excludeSet?.has(statusId)).map(
                (statusId) => ({
                    value: statusId,
                    label: t(`status.${statusId}`),
                    kind: 'status' as const,
                }),
            ),
        [t, excludeSet],
    );

    const selectedTypes = useMemo(
        () => typeOptions.filter((opt) => tasksType?.includes(opt.value)),
        [typeOptions, tasksType],
    );

    const selectedStatuses = useMemo(
        () => statusOptions.filter((opt) => tasksStatus?.includes(opt.value)),
        [statusOptions, tasksStatus],
    );

    const hasActiveFilter = Boolean(
        tasksStatus?.length ||
            tasksType?.length ||
            tasksExecutor?.length ||
            tasksController?.length ||
            filterLeadId ||
            filterObjectId ||
            onlyPersonal,
    );

    const applyTypes = (options: MultiValue<FilterOption>) => {
        const ids = options.map((o) => o.value);
        setTasksType(ids.length ? ids : null);
        setPage(1);
    };

    const applyStatuses = (options: MultiValue<FilterOption>) => {
        const ids = options.map((o) => o.value);
        setTasksStatus(ids.length ? ids : null);
        setPage(1);
    };

    const applyExecutor = (user: UserType | null) => {
        setExecutorUser(user);
        onExecutorUserChange?.(user);
        setTasksExecutor(user?.ID ? [Number(user.ID)] : null);
        if (!user) setExecutorPickerOpen(false);
        setPage(1);
    };

    const applyController = (user: UserType | null) => {
        setControllerUser(user);
        onControllerUserChange?.(user);
        setTasksController(user?.ID ? [Number(user.ID)] : null);
        if (!user) setControllerPickerOpen(false);
        setPage(1);
    };

    const applyLead = (next: LeadType | null) => {
        setLead(next);
        onLeadChange?.(next);
        setFilterLeadId(next?.ID ? Number(next.ID) : null);
        if (!next) setLeadPickerOpen(false);
        if (next) setOnlyPersonal(false);
        setPage(1);
    };

    const applyObject = (next: ObjectType | null) => {
        setObject(next);
        onObjectChange?.(next);
        setFilterObjectId(next?.ID ? Number(next.ID) : null);
        if (!next) setObjectPickerOpen(false);
        if (next) setOnlyPersonal(false);
        setPage(1);
    };

    const applyOnlyPersonal = (checked: boolean) => {
        setOnlyPersonal(checked);
        if (checked) {
            setLead(null);
            setObject(null);
            setLeadPickerOpen(false);
            setObjectPickerOpen(false);
            setFilterLeadId(null);
            setFilterObjectId(null);
            onLeadChange?.(null);
            onObjectChange?.(null);
        }
        setPage(1);
    };

    const handleReset = () => {
        setTasksType(null);
        setTasksStatus(null);
        setTasksExecutor(null);
        setTasksController(null);
        setFilterLeadId(null);
        setFilterObjectId(null);
        setOnlyPersonal(false);
        setExecutorUser(null);
        setControllerUser(null);
        setLead(null);
        setObject(null);
        setExecutorPickerOpen(false);
        setControllerPickerOpen(false);
        setLeadPickerOpen(false);
        setObjectPickerOpen(false);
        onExecutorUserChange?.(null);
        onControllerUserChange?.(null);
        onLeadChange?.(null);
        onObjectChange?.(null);
        setPage(1);
        setShow(false);
    };

    const handleDropdownToggle = (nextShow: boolean, meta?: { source?: string }) => {
        if (nextShow) {
            setShow(true);
            return;
        }
        // Закриваємо лише кліком по воронці; хрестики селекту не повинні закривати фільтр
        if (meta?.source === 'click') {
            setShow(false);
        }
    };

    return (
        <EToolTip title={t('toolTaskFilter')}>
            <span ref={rootRef} className="d-inline-block">
                <Dropdown
                    autoClose={false}
                    show={show}
                    onToggle={(next, meta) => handleDropdownToggle(next, meta)}
                >
                    <Dropdown.Toggle
                        className="fs-14 px-2 ms-1"
                        size="sm"
                        variant={hasActiveFilter ? 'danger' : 'primary'}
                        id="dropdown-tasks-filter"
                    >
                        <i className="bi bi-funnel fs-14 p-0"></i>
                    </Dropdown.Toggle>

                    <Dropdown.Menu
                        className="task-filter-menu p-3"
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => {
                            const target = e.target as HTMLElement | null;
                            if (
                                target?.closest('.task-filter-select__clear-indicator') ||
                                target?.closest('.task-filter-select__multi-value__remove')
                            ) {
                                e.stopPropagation();
                            }
                        }}
                    >
                        <div className="mb-3">
                            <label className="form-label text-muted fs-12 mb-1">
                                {t('filterType')}
                            </label>
                            <Select<FilterOption, true>
                                isMulti
                                isClearable
                                isSearchable
                                options={typeOptions}
                                value={selectedTypes}
                                onChange={applyTypes}
                                placeholder={t('filterType')}
                                className="w-100 task-filter-select"
                                classNamePrefix="task-filter-select"
                                styles={selectTheme}
                                formatOptionLabel={formatTypeOption}
                                menuPortalTarget={
                                    typeof document !== 'undefined' ? document.body : null
                                }
                                menuPosition="fixed"
                                menuShouldScrollIntoView={false}
                                closeMenuOnSelect={false}
                                hideSelectedOptions={false}
                            />
                        </div>

                        <div className="mb-3">
                            <label className="form-label text-muted fs-12 mb-1">
                                {t('filterStatus')}
                            </label>
                            <Select<FilterOption, true>
                                isMulti
                                isClearable
                                isSearchable={false}
                                options={statusOptions}
                                value={selectedStatuses}
                                onChange={applyStatuses}
                                placeholder={t('filterStatus')}
                                className="w-100 task-filter-select"
                                classNamePrefix="task-filter-select"
                                styles={selectTheme}
                                formatOptionLabel={formatStatusOption}
                                menuPortalTarget={
                                    typeof document !== 'undefined' ? document.body : null
                                }
                                menuPosition="fixed"
                                menuShouldScrollIntoView={false}
                                closeMenuOnSelect={false}
                                hideSelectedOptions={false}
                            />
                        </div>

                        {showExecutorFilter || showControllerFilter || canFilterRelated ? (
                            <div className="mb-3">
                                {showExecutorFilter ? (
                                    <div className="mb-2">
                                        {executorUser || executorPickerOpen ? (
                                            <UserSelected
                                                value={executorUser}
                                                onChange={applyExecutor}
                                                title={t('labelExecutor')}
                                                maxHeight={180}
                                                smallMode
                                                className="mb-0"
                                                rightElement={
                                                    executorPickerOpen && !executorUser ? (
                                                        <button
                                                            type="button"
                                                            className="btn btn-link p-0 align-baseline fs-12"
                                                            onClick={() => setExecutorPickerOpen(false)}
                                                        >
                                                            {t('filterCancelPick')}
                                                        </button>
                                                    ) : null
                                                }
                                            />
                                        ) : (
                                            <button
                                                type="button"
                                                className="btn btn-link p-0 align-baseline fs-12"
                                                onClick={() => setExecutorPickerOpen(true)}
                                            >
                                                <i className="bi bi-person-plus me-1"></i>
                                                {t('filterPickExecutor')}
                                            </button>
                                        )}
                                    </div>
                                ) : null}

                                {showControllerFilter ? (
                                    <div className="mb-2">
                                        {controllerUser || controllerPickerOpen ? (
                                            <UserSelected
                                                value={controllerUser}
                                                onChange={applyController}
                                                title={t('labelController')}
                                                maxHeight={180}
                                                smallMode
                                                className="mb-0"
                                                rightElement={
                                                    controllerPickerOpen && !controllerUser ? (
                                                        <button
                                                            type="button"
                                                            className="btn btn-link p-0 align-baseline fs-12"
                                                            onClick={() => setControllerPickerOpen(false)}
                                                        >
                                                            {t('filterCancelPick')}
                                                        </button>
                                                    ) : null
                                                }
                                            />
                                        ) : (
                                            <button
                                                type="button"
                                                className="btn btn-link p-0 align-baseline fs-12"
                                                onClick={() => setControllerPickerOpen(true)}
                                            >
                                                <i className="bi bi-person-plus me-1"></i>
                                                {t('filterPickController')}
                                            </button>
                                        )}
                                    </div>
                                ) : null}

                                {executorUser && controllerUser ? (
                                    <div
                                        className="text-muted mb-2"
                                        style={{ fontSize: '0.65rem', lineHeight: 1.35 }}
                                    >
                                        {t('filterUsersOrHint')}
                                    </div>
                                ) : null}

                                {canFilterRelated && !onlyPersonal ? (
                                    <>
                                        {!object && !objectPickerOpen ? (
                                            <div className="mb-2">
                                                {lead || leadPickerOpen ? (
                                                    <LeadSelected
                                                        value={lead}
                                                        onChange={applyLead}
                                                        title={t('labelLead')}
                                                        maxHeight={180}
                                                        className="mb-0"
                                                        statuses={TASK_FILTER_LEAD_STATUSES}
                                                        rightElement={
                                                            leadPickerOpen && !lead ? (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-link p-0 align-baseline fs-12"
                                                                    onClick={() => setLeadPickerOpen(false)}
                                                                >
                                                                    {t('filterCancelPick')}
                                                                </button>
                                                            ) : null
                                                        }
                                                    />
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className="btn btn-link p-0 align-baseline fs-12"
                                                        onClick={() => setLeadPickerOpen(true)}
                                                    >
                                                        <i className="bi bi-person-lines-fill me-1"></i>
                                                        {t('filterPickLead')}
                                                    </button>
                                                )}
                                            </div>
                                        ) : null}

                                        {!lead && !leadPickerOpen ? (
                                            <div className="mb-2">
                                                {object || objectPickerOpen ? (
                                                    <ObjectSelected
                                                        value={object}
                                                        onChange={applyObject}
                                                        title={t('labelObject')}
                                                        maxHeight={180}
                                                        className="mb-0"
                                                        statuses={TASK_FILTER_OBJECT_STATUSES}
                                                        rightElement={
                                                            objectPickerOpen && !object ? (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-link p-0 align-baseline fs-12"
                                                                    onClick={() => setObjectPickerOpen(false)}
                                                                >
                                                                    {t('filterCancelPick')}
                                                                </button>
                                                            ) : null
                                                        }
                                                    />
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className="btn btn-link p-0 align-baseline fs-12"
                                                        onClick={() => setObjectPickerOpen(true)}
                                                    >
                                                        <i className="bi bi-building me-1"></i>
                                                        {t('filterPickObject')}
                                                    </button>
                                                )}
                                            </div>
                                        ) : null}
                                    </>
                                ) : null}

                                {canFilterRelated ? (
                                    <div className="form-check form-switch mb-0">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            role="switch"
                                            id="task-filter-only-personal"
                                            checked={onlyPersonal}
                                            onChange={(e) =>
                                                applyOnlyPersonal(e.currentTarget.checked)
                                            }
                                        />
                                        <label
                                            className="form-check-label"
                                            htmlFor="task-filter-only-personal"
                                        >
                                            <span className="d-block fs-12">
                                                {t('filterOnlyPersonal')}
                                            </span>
                                            <span
                                                className="d-block text-muted"
                                                style={{ fontSize: '0.65rem', lineHeight: 1.35 }}
                                            >
                                                {t('filterOnlyPersonalHint')}
                                            </span>
                                        </label>
                                    </div>
                                ) : null}
                            </div>
                        ) : null}

                        <div className="text-end">
                            <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                className="mb-0 min70px"
                                style={{ height: 24 }}
                                disabled={!hasActiveFilter}
                                onClick={handleReset}
                            >
                                {t('common:buttons.reset')}
                            </Button>
                            <Button
                                type="button"
                                variant="primary"
                                size="sm"
                                className="mb-0 ms-2 min70px"
                                style={{ height: 24 }}
                                onClick={() => setShow(false)}
                            >
                                {t('common:buttons.close')}
                            </Button>
                        </div>
                    </Dropdown.Menu>
                </Dropdown>
            </span>
        </EToolTip>
    );
};

export default TaskFilter;
